import React, { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';

import { formatCurrency } from '../../utils/formatCurrency';

interface ChartDynamicProps {
  title?: string;
  data?: number[];
  categories?: string[];
  enableFilters?: boolean;
  onFilterChange?: (year: number, month?: number) => void;
  type?: 'monthly' | 'daily';
}

interface ChartDynamicState {
  series: {
    name: string;
    data: number[];
  }[];
}

const ChartDynamic: React.FC<ChartDynamicProps> = ({
  title = 'Gráfico dinámico',
  data = [],
  categories = [],
  enableFilters = false,
  onFilterChange,
  type = 'monthly',
}) => {
  const [state, setState] = useState<ChartDynamicState>({
    series: [
      {
        name: 'Ventas',
        data: [],
      },
    ],
  });

  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear()
  );

  const [selectedMonth, setSelectedMonth] = useState<number>(
    new Date().getMonth() + 1
  );

  // =====================================================
  // 📦 Backend Data
  // =====================================================

  useEffect(() => {
    setState({
      series: [
        {
          name: 'Ventas',
          data,
        },
      ],
    });
  }, [data]);

  // =====================================================
  // 🎨 Apex Config
  // =====================================================

  const options: ApexOptions = {
    colors: ['#3C50E0'],

    chart: {
      type: 'bar',
      height: '100%',
      toolbar: {
        show: false,
      },
      fontFamily: 'Satoshi, sans-serif',
    },

    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        borderRadius: 6,
      },
    },

    dataLabels: {
      enabled: false,
    },

    stroke: {
      show: true,
      width: 2,
      colors: ['transparent'],
    },

    xaxis: {
      categories,

      labels: {
        style: {
          fontSize: '12px',
          colors: '#9CA3AF',
        },
        rotateAlways: false,
      },

      axisBorder: {
        show: false,
      },

      axisTicks: {
        show: false,
      },
    },

    yaxis: {
      labels: {
        formatter: (value) => {
          if (value >= 1000000) {
            return `$${(value / 1000000).toFixed(1)}M`;
          }

          if (value >= 1000) {
            return `$${(value / 1000).toFixed(1)}K`;
          }

          return `$${formatCurrency(value)}`;
        },

        style: {
          fontSize: '12px',
          colors: '#9CA3AF',
        },
      },
    },

    grid: {
      borderColor: '#E5E7EB',
      strokeDashArray: 4,
      padding: {
        left: 10,
        right: 10,
      },
    },

    tooltip: {
      theme: 'dark',

      y: {
        formatter: (value) => `$${formatCurrency(value)}`,
      },
    },

    fill: {
      opacity: 0.9,
    },

    responsive: [
      {
        breakpoint: 1280,
        options: {
          plotOptions: {
            bar: {
              columnWidth: '65%',
            },
          },
        },
      },

      {
        breakpoint: 1024,
        options: {
          chart: {
            height: 300,
          },

          plotOptions: {
            bar: {
              columnWidth: '70%',
            },
          },

          xaxis: {
            labels: {
              rotate: -45,
            },
          },
        },
      },

      {
        breakpoint: 768,
        options: {
          chart: {
            height: 260,
          },

          plotOptions: {
            bar: {
              columnWidth: '75%',
            },
          },

          xaxis: {
            labels: {
              style: {
                fontSize: '11px',
              },
              rotate: -45,
            },
          },
        },
      },

      {
        breakpoint: 480,
        options: {
          chart: {
            height: 240,
          },

          plotOptions: {
            bar: {
              columnWidth: '80%',
            },
          },

          xaxis: {
            labels: {
              style: {
                fontSize: '10px',
              },
              rotate: -45,
            },
          },

          yaxis: {
            labels: {
              style: {
                fontSize: '10px',
              },
            },
          },

          legend: {
            fontSize: '10px',
          },
        },
      },
    ],
  };

  // =====================================================
  // ⚙️ Filters
  // =====================================================

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const year = Number(e.target.value);

    setSelectedYear(year);

    onFilterChange?.(year, type === 'daily' ? selectedMonth : undefined);
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const month = Number(e.target.value);

    setSelectedMonth(month);

    onFilterChange?.(selectedYear, month);
  };

  return (
    <div className="col-span-12 rounded-xl border border-stroke bg-white px-4 py-5 shadow-default transition-all duration-300 ease-in-out dark:border-strokedark dark:bg-boxdark sm:px-6 lg:px-8">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-base font-semibold text-black dark:text-white md:text-lg">
          {title}
        </h3>

        {enableFilters && (
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={selectedYear}
              onChange={handleYearChange}
              className="border-gray-300 text-gray-700 rounded-md border bg-white px-2.5 py-1.5 text-sm shadow-sm transition focus:border-primary focus:ring focus:ring-primary/20 dark:border-strokedark dark:bg-boxdark dark:text-white"
            >
              {[2023, 2024, 2025, 2026].map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>

            {type === 'daily' && (
              <select
                value={selectedMonth}
                onChange={handleMonthChange}
                className="border-gray-300 text-gray-700 rounded-md border bg-white px-2.5 py-1.5 text-sm shadow-sm transition focus:border-primary focus:ring focus:ring-primary/20 dark:border-strokedark dark:bg-boxdark dark:text-white"
              >
                {[
                  'Ene',
                  'Feb',
                  'Mar',
                  'Abr',
                  'May',
                  'Jun',
                  'Jul',
                  'Ago',
                  'Sep',
                  'Oct',
                  'Nov',
                  'Dic',
                ].map((month, index) => (
                  <option key={index + 1} value={index + 1}>
                    {month}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}
      </div>

      <div className="w-full overflow-x-auto">
        <ReactApexChart
          options={options}
          series={state.series}
          type="bar"
          height={350}
        />
      </div>
    </div>
  );
};

export default ChartDynamic;
